import FilterListIcon from '@material-ui/icons/FilterList'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { styled } from 'styled-components'

import { isConsole } from '../../config/config'
import { HandlerOf, truthy } from '../../helpers/typeHelper'
import { useNoteAvailableTags } from '../../hooks/query/useNoteAvailableTags'
import { ApiVersion } from '../../models'
import { NoteRecordType, NotesAvailabelTag } from '../../models/note'
import { bgGray, darkGray, lightGray1, lightGray2 } from '../../theme/colors'
import { centeredContent } from '../../theme/mixins'
import { header4 } from '../../theme/typography'
import { InlineBlock } from '../Base/Display'
import { FlexBox } from '../Base/FlexBox'
import { CheckboxDropdown } from '../Dropdown/CheckboxDropdown'
import { Text } from '../Text/Text'
import { createTagLabelOption } from './NoteComposerAddTags'

interface NotesTagsFilterProps {
  recordId: string
  recordType: NoteRecordType
  isTimeline?: boolean
  showOnlyActivity: boolean
  setShowOnlyActivity: HandlerOf<boolean>
  filterTags: string[] | null
  setFilterTags: HandlerOf<string[] | null>
  apiVersion?: ApiVersion
}

export function NotesTagsFilter({
  recordId,
  recordType,
  filterTags,
  setFilterTags,
  isTimeline,
  showOnlyActivity,
  setShowOnlyActivity,
  apiVersion,
}: NotesTagsFilterProps) {
  const { tags: availableTags } = useNoteAvailableTags(
    { recordId, recordType, apiVersion },
    { enabled: true }
  )

  if (!availableTags.length || !isConsole()) return null

  function handleTagChange(tag: NotesAvailabelTag) {
    setShowOnlyActivity(false)
    setFilterTags(
      filterTags?.includes(tag.value)
        ? filterTags.filter((t) => t !== tag.value)
        : [...(filterTags || []), tag.value]
    )
  }

  function handleActivityChange() {
    setShowOnlyActivity(!showOnlyActivity)
    setFilterTags(null)
  }

  function createTagsOptions() {
    const tagOptions = availableTags?.map((tag) => ({
      key: tag.label,
      label: tag.label,
      value: filterTags?.includes(tag.value) || false,
      onChange: () => handleTagChange(tag),
    }))

    if (isTimeline) {
      return [
        createTagLabelOption('Notes'),
        ...tagOptions,
        createTagLabelOption('Other events', false),
        {
          key: 'Suspension',
          label: 'Suspension',
          value: showOnlyActivity,
          onChange: () => handleActivityChange(),
        },
      ].filter(truthy)
    }

    return tagOptions
  }

  return (
    <>
      <CheckboxDropdown
        label="Trailer type"
        placeholder="Trailer"
        options={createTagsOptions()}
        maxHeight={400}
        width="250px"
      >
        <FlexBox gap="5px" alignItems="center" styles="cursor: pointer">
          <StyledFilterListIcon />
          <Text variant="header3" color={lightGray1}>
            Filter
          </Text>
        </FlexBox>
      </CheckboxDropdown>
      {filterTags?.length ? (
        <Tags>
          {filterTags?.map((tag) => (
            <InlineBlock key={tag}>
              <Tag>
                {availableTags?.find((t) => t.value === tag)?.label}
                <RemoveTagIcon onClick={() => setFilterTags(filterTags.filter((t) => t !== tag))} />
              </Tag>
            </InlineBlock>
          ))}
        </Tags>
      ) : null}

      {showOnlyActivity ? (
        <Tags>
          <InlineBlock>
            <Tag>
              Suspension
              <RemoveTagIcon onClick={() => setShowOnlyActivity(false)} />
            </Tag>
          </InlineBlock>
        </Tags>
      ) : null}
    </>
  )
}

const StyledFilterListIcon = styled(FilterListIcon)`
  color: ${lightGray1};
  margin-top: -5px;
`

const Tags = styled.div`
  margin-top: 10px;
`

const Tag = styled.div`
  ${header4};
  ${centeredContent};
  padding: 5px 12px 4px;
  border-radius: 3px;
  background-color: ${bgGray};
  color: ${darkGray};
  gap: 5px;
  margin-right: 10px;
`

const RemoveTagIcon = styled(HighlightOffIcon)`
  font-size: 16px !important;
  color: ${lightGray2};
  margin-top: -2px;
  cursor: pointer;
`
